import PropTypes from "prop-types";
import { useEffect } from "react";
import { Download, History, X } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { downloadCustomLeadsImportErrors } from "@/lib/api";

function formatImportStatus(status) {
  const normalized = String(status || "completed").toLowerCase();
  if (normalized === "completed") return "Completed";
  if (normalized === "failed") return "Failed";
  if (normalized === "processing") return "Processing";
  return status || "Completed";
}

function importStatusClass(status) {
  const normalized = String(status || "completed").toLowerCase();
  if (normalized === "failed") return "abm-import-status--failed";
  if (normalized === "processing") return "abm-import-status--processing";
  return "abm-import-status--completed";
}

function ImportHistoryTable({ imports }) {
  if (!imports.length) {
    return (
      <div className="abm-import-history-empty" data-testid="custom-leads-import-history-empty">
        No imports yet. Upload an Excel file to see import history here.
      </div>
    );
  }

  return (
    <div className="abm-import-history-table-wrap">
      <table className="abm-import-history-table">
        <thead>
          <tr>
            <th>File</th>
            <th>Status</th>
            <th>Imported</th>
            <th>Skipped</th>
            <th>Failed</th>
            <th>Total rows</th>
            <th>Imported at</th>
            <th>Logs</th>
          </tr>
        </thead>
        <tbody>
          {imports.map((imp) => {
            const skipped = (imp.duplicateFileCount || 0) + (imp.duplicateDbCount || 0);
            const failed = imp.failedCount || 0;
            const hasErrors = failed > 0 || skipped > 0;
            return (
              <tr key={imp.id} data-testid={`custom-leads-import-row-${imp.id}`}>
                <td className="abm-import-file" title={imp.fileName}>
                  {imp.fileName}
                </td>
                <td>
                  <span className={`abm-import-status ${importStatusClass(imp.status)}`}>
                    {formatImportStatus(imp.status)}
                  </span>
                </td>
                <td className="abm-import-stat abm-import-stat--success">{imp.successCount ?? 0}</td>
                <td className="abm-import-stat">{skipped || "—"}</td>
                <td className={`abm-import-stat${failed ? " abm-import-stat--failed" : ""}`}>
                  {failed || "—"}
                </td>
                <td className="abm-import-stat">{imp.totalRows ?? "—"}</td>
                <td className="abm-import-date">{formatDateTime(imp.createdAt)}</td>
                <td className="abm-import-actions">
                  {hasErrors ? (
                    <button
                      type="button"
                      className="btn abm-import-errors-btn"
                      onClick={async () => {
                        try {
                          await downloadCustomLeadsImportErrors(
                            imp.id,
                            `import_errors_${imp.fileName || imp.id}.csv`,
                          );
                        } catch (e) {
                          toast.error(e?.message || "Could not download import log");
                        }
                      }}
                      data-testid={`custom-leads-import-errors-${imp.id}`}
                    >
                      <Download size={12} /> Errors CSV
                    </button>
                  ) : (
                    <span className="abm-import-actions-muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

ImportHistoryTable.propTypes = {
  imports: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    fileName: PropTypes.string,
    successCount: PropTypes.number,
    failedCount: PropTypes.number,
    duplicateFileCount: PropTypes.number,
    duplicateDbCount: PropTypes.number,
    totalRows: PropTypes.number,
    status: PropTypes.string,
    createdAt: PropTypes.string,
  })).isRequired,
};

export function AbmImportHistoryModal({ open, onOpenChange, imports }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div className="rnc-platform">
      <dialog
        open
        className="modal-overlay abm-import-history-modal"
        aria-labelledby="abm-import-history-title"
      >
        <button 
          type="button"
          aria-label="Close modal"
          className="modal-backdrop-catcher"
          onClick={() => onOpenChange(false)}
          style={{ position: "absolute", inset: 0, zIndex: -1, background: "transparent", border: "none", padding: 0, margin: 0 }}
        />
        <div className="modal-box wide" data-testid="custom-leads-import-history">
          <div className="modal-header">
            <div>
              <h2 id="abm-import-history-title">
                <History size={16} /> Import history
              </h2>
              <p>
                {(() => {
                  const importText = imports.length === 1 ? "import" : "imports";
                  return imports.length
                    ? `${imports.length} ${importText} recorded for this workspace.`
                    : "Upload history for ABM List Excel imports.";
                })()}
              </p>
            </div>
            <button
              type="button"
              className="modal-close"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          <div className="modal-body abm-import-history-modal-body">
            <ImportHistoryTable imports={imports} />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn primary" onClick={() => onOpenChange(false)}>
              Close
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}

AbmImportHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  imports: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    fileName: PropTypes.string,
    successCount: PropTypes.number,
    failedCount: PropTypes.number,
    duplicateFileCount: PropTypes.number,
    duplicateDbCount: PropTypes.number,
    totalRows: PropTypes.number,
    status: PropTypes.string,
    createdAt: PropTypes.string,
  })).isRequired,
};
